/**
 * VoiceRecorder — self-contained mic control for the Copilot input.
 *
 * States: idle (mic) -> recording (elapsed timer + stop button) ->
 * transcribing (spinner) -> back to idle. Degrades honestly:
 *   - `GET /voice/config` says `enabled:false` -> disabled button, French
 *     tooltip, NEVER a fake transcript.
 *   - Mic permission denied -> inline French error, button stays usable to retry.
 *   - Backend 503 (`voice_unavailable`) -> inline French error, never invents text.
 *   - Backend 402 (out of AI credits) -> NOT handled here: `transcribe()`
 *     (src/lib/api/voice.ts) already let the global insufficient-credits
 *     event fire via the shared API client, so this component just resets
 *     its own local state and does not render a second paywall.
 *
 * Fully self-contained (no dependency on CopilotWidget/copilot-store) — the
 * integration lane wires it in via `onTranscript`.
 */

import { useEffect, useRef, useState } from 'react';

import { ApiError } from '@/lib/api/client';
import {
  getVoiceConfig,
  isVoiceOutOfCredits,
  isVoiceTooLarge,
  isVoiceUnavailable,
  transcribe,
  type VoiceConfig,
} from '@/lib/api/voice';

import { CreditCost } from '@/features/credits';

import { useVoiceRecorder } from './useVoiceRecorder';
import { useT } from '@/i18n';

export interface VoiceRecorderProps {
  /** Called with the final transcript once transcription succeeds. Prefers the
   * translated text (when `translateTo` was supplied and translation ran). */
  onTranscript: (text: string) => void;
  /** Called on any failure with a French, user-facing message. */
  onError?: (message: string) => void;
  /** ISO language hint for Whisper (improves accuracy). */
  language?: string;
  /** Run the server-side ASR-cleanup pass. Default true. */
  correct?: boolean;
  /** Target language code/name to translate the transcript into. */
  translateTo?: string;
  /** Hard recording ceiling in seconds. Default 60. */
  maxDurationSec?: number;
  /** Extra classes for the wrapping element. */
  className?: string;
  /** Force-disable regardless of server config (e.g. a parent-level gate). */
  disabled?: boolean;
}

function formatElapsed(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M19 11a7 7 0 0 1-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function VoiceRecorder({
  onTranscript,
  onError,
  language,
  correct = true,
  translateTo,
  maxDurationSec = 60,
  className,
  disabled,
}: VoiceRecorderProps) {
  const { t } = useT();
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleStopRef = useRef<() => void>(() => {});
  const recorder = useVoiceRecorder({
    maxDurationSec,
    onMaxDurationReached: () => handleStopRef.current(),
  });

  useEffect(() => {
    let alive = true;
    getVoiceConfig()
      .then((cfg) => {
        if (alive) {
          setConfig(cfg);
          setConfigLoaded(true);
        }
      })
      .catch(() => {
        // A real backend failure (500/401/403) — not "voice absent". Stop the
        // "loading" state and say so rather than leaving the control hanging.
        if (alive) {
          setConfigLoaded(true);
          setLocalError(t('shell.features.copilotVoiceRecorder.theVoiceServiceIsTemporarilyUnavailable'));
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  // Reset our own inline error whenever the recorder reports a fresh one
  // (permission denied / unsupported) so the two never show stale text.
  useEffect(() => {
    if (recorder.error) setLocalError(recorder.error);
  }, [recorder.error]);

  const reportError = (message: string) => {
    setLocalError(message);
    onError?.(message);
  };

  const handleStart = async () => {
    setLocalError(null);
    await recorder.start();
  };

  const handleStop = async () => {
    const recording = await recorder.stop();
    if (!recording || recording.blob.size === 0) {
      reportError(t('shell.features.copilotVoiceRecorder.noSoundCapturedTryAgainSpeaking'));
      return;
    }
    setTranscribing(true);
    setLocalError(null);
    try {
      const result = await transcribe(recording.blob, {
        language,
        correct,
        translate_to: translateTo,
      });
      onTranscript(result.translated_text || result.text);
    } catch (err) {
      if (isVoiceOutOfCredits(err)) {
        // The global psn:ai-credits-insufficient event already fired inside
        // transcribe()/apiFetch — the shared refill modal/toast handles it.
        // No bespoke voice paywall here.
      } else if (isVoiceUnavailable(err)) {
        reportError(err.reason || t('shell.features.copilotVoiceRecorder.speechRecognitionTemporarilyUnavailableT'));
      } else if (isVoiceTooLarge(err)) {
        reportError(err.message);
      } else if (err instanceof ApiError && err.status === 403) {
        // A refused write (VIEWER) — never echo the backend's raw, English role list
        // (« Requires one of roles: admin, business, … ») to the user.
        reportError(t('shell.features.copilotVoiceRecorder.accessDeniedInsufficientRightsToDictate'));
      } else if (err instanceof ApiError) {
        const body = err.body as { detail?: unknown } | null;
        const detail = typeof body?.detail === 'string' ? body.detail : undefined;
        reportError(detail || t('shell.features.copilotVoiceRecorder.theTranscriptionFailedTryAgain'));
      } else {
        reportError(t('shell.features.copilotVoiceRecorder.theTranscriptionFailedTryAgain'));
      }
    } finally {
      setTranscribing(false);
    }
  };
  handleStopRef.current = () => void handleStop();

  const handleCancel = () => {
    recorder.cancel();
    setLocalError(null);
  };

  const voiceDisabledByServer = configLoaded && !config?.enabled;
  const unsupported = recorder.status === 'unsupported';
  const isDisabled = Boolean(disabled) || !configLoaded || voiceDisabledByServer || unsupported || transcribing;

  const tooltip = disabled
    ? undefined
    : !configLoaded
      ? t('shell.features.copilotVoiceRecorder.checkingAvailability')
      : voiceDisabledByServer
        ? t('shell.features.copilotVoiceRecorder.speechRecognitionNotConfigured')
        : unsupported
          ? t('shell.features.copilotVoiceRecorder.audioRecordingNotSupportedByThis')
          : recorder.status === 'recording'
            ? t('shell.features.copilotVoiceRecorder.stopRecordingV', { v: formatElapsed(recorder.elapsedSec) })
            : transcribing
              ? t('shell.features.copilotVoiceRecorder.transcribing')
              : t('shell.features.copilotVoiceRecorder.voiceInput');

  const wrapperClass = ['inline-flex items-center gap-2', className].filter(Boolean).join(' ');

  if (recorder.status === 'recording') {
    return (
      <div className={wrapperClass}>
        <button
          type="button"
          onClick={() => void handleStop()}
          title={tooltip}
          aria-label={t('shell.features.copilotVoiceRecorder.stopVoiceRecordingVElapsed', { v: formatElapsed(recorder.elapsedSec) })}
          className="inline-flex items-center gap-1.5 rounded-token border border-danger/40 bg-danger/10 px-2.5 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-danger" />
          </span>
          <StopIcon />
          <span className="tabular-nums">{formatElapsed(recorder.elapsedSec)}</span>
        </button>
        {/* Stopping triggers the metered transcription (POST /ai/voice/transcribe). */}
        <CreditCost feature="copilot_transcribe" />
        <button
          type="button"
          onClick={handleCancel}
          aria-label={t('shell.features.copilotVoiceRecorder.cancelTheRecording')}
          title={t('shell.features.copilotVoiceRecorder.cancel')}
          className="text-xs text-muted hover:text-foreground"
        >{t('shell.features.copilotVoiceRecorder.cancel')}</button>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        onClick={() => void handleStart()}
        disabled={isDisabled}
        title={tooltip}
        aria-label={transcribing ? t('shell.features.copilotVoiceRecorder.transcriptionInProgress') : t('shell.features.copilotVoiceRecorder.startVoiceInput')}
        aria-busy={transcribing}
        className="inline-flex items-center justify-center rounded-token border border-border bg-transparent p-1.5 text-muted transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      >
        {transcribing ? <Spinner /> : <MicIcon />}
      </button>
      {/* Sibling of the mic button (never inside it): the price of the metered
          transcription that ends this recording. Hidden when voice is off. */}
      {configLoaded && !voiceDisabledByServer && !unsupported && <CreditCost feature="copilot_transcribe" />}
      {localError && (
        <p role="alert" className="text-xs text-danger">
          {localError}
        </p>
      )}
    </div>
  );
}
